import { ERROR_CODES } from '@roll-web/constants';
import {
  payByAlipayPlus,
  payByApplePay,
  payByCarriersAu,
  payByCarriersDocomo,
  payByCarriersSoftbank,
  payByCredit,
  payByGooglePay,
  payByPayPay,
  payByPlayTicket,
  payByUnionpay,
} from 'src/services/orders/paymentHandler';
import { convertErrorToErrorDescription } from '~/src/services/errors';
import { OrderDetail } from '~/types/resources/orderDetail';

export type OnSuccessCallback =
  | OnSuccessInstantPaymentCallback
  | OnSuccessRedirectCallback
  | OnSuccessCarrierCallback;

export type OnSuccessInstantPaymentCallback = (
  order_id: string
) => Promise<void>;

export type OnSuccessRedirectCallback = (
  order_id: string,
  redirect_url: string
) => Promise<void>;

export type OnSuccessCarrierCallback = (
  order_id: string,
  access_id: string,
  redirect_url: string,
  auToken: string
) => Promise<void>;

export type OnErrorCallback = (
  page_error_title: string,
  page_error_body?: string,
  page_error_extraText?: string
) => Promise<void>;

export type PayOrderInput =
  | {
      method: 'play_ticket';
      order_id: string;
      user_token: string;
      onSuccessCallback: OnSuccessInstantPaymentCallback;
      onErrorCallback: OnErrorCallback;
      playTicketInput: {
        id: string;
      };
    }
  | {
      method: 'credit';
      order_id: string;
      cardSeq: string;
      user_token: string;
      returnUrl: string;
      onSuccessCallback: OnSuccessInstantPaymentCallback;
      onSuccessRedirectCallback: OnSuccessRedirectCallback;
      onErrorCallback: OnErrorCallback;
    }
  | {
      method: 'paypay' | 'alipay+';
      order_id: string;
      user_token: string;
      returnUrl: string;
      onSuccessCallback: OnSuccessRedirectCallback;
      onErrorCallback: OnErrorCallback;
    }
  | {
      method: 'applepay';
      domainName: string;
      label: string;
      user_token: string;
      order_id: OrderDetail['id'];
      applePaySession: ApplePaySession;
      onErrorCallback: OnErrorCallback;
      onSuccessCallback: OnSuccessInstantPaymentCallback;
    }
  | {
      method: 'googlepay';
      totalPrice: string;
      user_token: string;
      order_id: OrderDetail['id'];
      onSuccessCallback: OnSuccessInstantPaymentCallback;
      onErrorCallback: OnErrorCallback;
    }
  | {
      method:
        | 'unionpay'
        | 'carriers:au'
        | 'carriers:docomo'
        | 'carriers:softbank';
      order_id: string;
      user_token: string;
      returnUrl: string;
      onSuccessCallback: OnSuccessCarrierCallback;
      onErrorCallback: OnErrorCallback;
    };

export const payOrder = async (input: PayOrderInput) => {
  const { order_id, method, user_token, onSuccessCallback } = input;

  switch (method) {
    case 'credit':
      await payByCredit(order_id, {
        cardSeq: input.cardSeq,
        user_token,
        returnUrl: input.returnUrl,
        onSuccessCallback: input.onSuccessCallback,
        onSuccessRedirectCallback: input.onSuccessRedirectCallback,
        onErrorCallback: input.onErrorCallback,
      });
      break;
    case 'paypay':
      await payByPayPay(order_id, {
        user_token,
        returnUrl: input.returnUrl,
        onSuccessCallback: onSuccessCallback as OnSuccessRedirectCallback,
        onErrorCallback: input.onErrorCallback,
      });
      break;
    case 'alipay+':
      await payByAlipayPlus(order_id, {
        user_token,
        returnUrl: input.returnUrl,
        onSuccessCallback: onSuccessCallback as OnSuccessRedirectCallback,
        onErrorCallback: input.onErrorCallback,
      });
      break;
    case 'unionpay':
      await payByUnionpay(order_id, {
        user_token,
        returnUrl: input.returnUrl,
        onSuccessCallback: onSuccessCallback as OnSuccessCarrierCallback,
        onErrorCallback: input.onErrorCallback,
      });
      break;
    case 'applepay':
      await payByApplePay({
        orderId: order_id,
        domainName: input.domainName,
        label: input.label,
        userToken: user_token,
        applePaySession: input.applePaySession,
        onErrorCallback: input.onErrorCallback,
        onSuccessCallback: onSuccessCallback as OnSuccessInstantPaymentCallback,
      });
      break;
    case 'googlepay':
      await payByGooglePay({
        totalPrice: input.totalPrice,
        userToken: user_token,
        orderId: order_id,
        onSuccessCallback: onSuccessCallback as OnSuccessInstantPaymentCallback,
        onErrorCallback: input.onErrorCallback,
      });
      break;
    case 'play_ticket':
      await payByPlayTicket({
        orderId: order_id,
        playTicketInput: input.playTicketInput,
        userToken: user_token,
        onSuccessCallback: onSuccessCallback,
        onErrorCallback: input.onErrorCallback,
      });
      break;
    case 'carriers:au':
      await payByCarriersAu(order_id, {
        user_token,
        returnUrl: input.returnUrl,
        onSuccessCallback: onSuccessCallback as OnSuccessCarrierCallback,
        onErrorCallback: input.onErrorCallback,
      });
      break;
    case 'carriers:docomo':
      await payByCarriersDocomo(order_id, {
        user_token,
        returnUrl: input.returnUrl,
        onSuccessCallback: onSuccessCallback as OnSuccessCarrierCallback,
        onErrorCallback: input.onErrorCallback,
      });
      break;
    case 'carriers:softbank':
      await payByCarriersSoftbank(order_id, {
        user_token,
        returnUrl: input.returnUrl,
        onSuccessCallback: onSuccessCallback as OnSuccessCarrierCallback,
        onErrorCallback: input.onErrorCallback,
      });
      break;
    default:
      throw convertErrorToErrorDescription(
        ERROR_CODES.PAYMENT_METHOD_NOT_SELECTED,
        '決済方法を選択してください'
      );
  }
};
